/**
 * @file   src\Pages\AddSubscription.tsx
 * @brief  AddSubscription page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, useRef, useEffect, Container, Badge, Button, Breadcrumb, Row, Col, useOutletContext, useNavigate, useLocation, useParams, React } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWInputGroup from '../components/CWInputGroup';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { MessageToaster } from '../utils/toastUtil';
import { addSubscriptionSchema } from '../validations/addSubscriptionSchema';
import { AMOUNT_MAX_LENGTH, WASH_DETAIL_MAX_LENGTH, WASH_NAME_MAX_LENGTH, WASH_NUMBER_MAX_LENGTH } from '../utils/constants';
import { AppContextType } from '../interfaces/generalInterface';
import CWMultiSelect from '../components/CWMultiSelect';
import GenerateLink from '../components/GenerateLink';
import CarWashStatus from '../components/CarWashStatus';
import axios from '../config/axiosConfig';
import { CompanyApi, LocationApi, SubscriptionApi } from '../utils/apiUrls';
import { LocationItem, Option, SubscriptionDataItem, SubscriptionItem } from '../interfaces/subscriptionInterface';
import CWTextarea from '../components/CWTextarea';
import { serviceType, userType } from '../utils/enums';
import CWCheckbox from '../components/CWCheckbox';
import SmallLoader from '../components/SmallLoader';
import { CommonMessages } from '../messages/validationMessages';
import { addSelfServeOnlySubscriptionSchema, addSelfServeSubscriptionSchema } from '../validations/addSelfServeSubscriptionSchema';

interface DiscountData {
  [key: string]: number | string; // allow dynamic keys like discount1...discount10
}
interface ErrorData {
  [key: string]: string;
}
const EditSelfServeSubscription = (props: any) => {
    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false
    // State to handle locations given in dropdown
    const [locations, setLocations] = useState<Option[]>([]);

    // State to handle subscription types given in dropdown
    const subscriptionTypes = [
        { label: "Single wash", value: "day" },
        { label: "Monthly", value: "month" },
    ];

    // To get path from which redirection was done
    const location = useLocation();

    const currentBayType = location?.state?.bayType||1

    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);

    // Initialize states for formdata
    const [subscriptionData, setSubscriptionData] = useState<Partial<SubscriptionDataItem>>({})

    const [isFamilyPlan,setIsFamilyPlan]=useState(false);
    const [familyPlanErrors,setFamilyPlanErrors] =  useState<ErrorData>({});
    // const [hasExtension, setHasExtension] = useState<boolean>(true);
    
    const [discountData, setDiscountData] = useState<DiscountData | null>({
        discount1: "",
        discount2: "",
        discount3: "",
        discount4: "",
        discount5: "",
        discount6: "",
        discount7: "",
        discount8: "",
        discount9: "",
        discount10: ""
    });
    

    // Initialise states for errors
    const [errors, setErrors] = useState({
        location: '',
        subscription: '',
        washname: '',
        washdetails: '',
        washnumber: '',
        maxwashperday: '',
        amount: '',
        preAuthAmount: '',
        trialAmount: '',
        trialPeriod: '',
        washTime:"",
        priceForExtraTime:"",
        hasVacuumWash: '',
        numberOfVacuumWashes: '',
        maxVacuumTime: ''
    });

    const param = useParams()
    // State to handle selected location
    const [selectedLocation, setSelectedLocation] = useState<Option[]>([]);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    const [companyDetails, setCompanyDetails] = useState<any>({});

    const navigate = useNavigate()

    const toaster = new MessageToaster();

    const [loading, setLoading] = useState(false);
    const [fullEditAllowed, setFullEditAllowed] = useState(false);

    const query = new URLSearchParams(location.search);
    const subscription_type = query.get('type') || 'month'


    useEffect(() => {
        getCompany()
        getSubscriptionData()
    }, [])


    useEffect(() => {
        // To display heading
        handleUpdate()
    }, [param.carWashId])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: subscription_type === 'day' ? (currentBayType==serviceType.Vacuum ? "Edit Single Use" :"Edit Single Wash") : 'Edit Subscription', url: (!carwash_owner) ? `/superadmin/${pageURLs.manage_subscriptions}/${param.carWashId}` : `/${pageURLs.manage_subscriptions}?type=${subscription_type}&bayType=${currentBayType}`, showBackButton: true, showMainMenu: true, activeLink: (!carwash_owner) ? "manage-car-wash" : "manage-subscriptions" });
    };

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${param.carWashId}`;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY + param1)
                : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
            setCompanyDetails(data.data)
            // To get locations under company
            await getCompanyLocations(data.data.carWashGUID)

        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    const setHasExtension = (e:any)=>{
            
             if(e.target.checked ==false)
             {
                setSubscriptionData({...subscriptionData,
                    // priceForExtraTime:'0',
                    canExtentSingleWash:e.target.checked}); 
             }
             else{
                 setSubscriptionData({...subscriptionData,
                     canExtentSingleWash:e.target.checked}); 
             }

    }

    const setFreeVacuumData = async (e: any) => {
        if (e.target.name === 'hasVacuumWash') {
            if (e.target.checked === true) {
                if(subscriptionData.subscription === 'day')
                {
                    setSubscriptionData({
                    ...subscriptionData,
                    'numberOfVacuumWashes':'1',
                    [e.target.name]: e.target.checked,
                });
                    
                }
                else{
                    setSubscriptionData({
                    ...subscriptionData,
                    [e.target.name]: e.target.checked,
                });
                }
                
            } else {
                setSubscriptionData({
                    ...subscriptionData,
                    [e.target.name]: e.target.checked,
                    ['hasVacuumWash']: false,
                    ['numberOfVacuumWashes']: '0',
                    ['maxVacuumTime']: '0',
                });
            }
        } 
    }

    const getCompanyLocations = async (carwashGuid: string) => {
        try {

            const { data } = (!carwash_owner) ?
                await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid)
                : await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setLocations(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getSubscriptionData = async () => {
        try {
            const { data } = (!carwash_owner) ?
                await axios.get(SubscriptionApi.GET_SUBSCRIPTION_DETAILS + param.id)
                :
                await axios.get(SubscriptionApi.CARWASH_OWNER_GET_SUBSCRIPTION_DETAILS + param.id)
            if (data.error === 0) {

                const subscription: SubscriptionItem = data.data[0]
                const transformedLocationArray: Option[] =
                    subscription.Locations.map(item => ({
                        value: item.LocationID,
                        label: item.LocationName
                    }))
                const subscriptionEditAllowed = (subscription.PurchasedStatus && subscription.PurchasedStatus === true)  ? false : true // If user already purchased subscription we are not allowing to edit details
                setFullEditAllowed(subscriptionEditAllowed)

                const subscriptionData: SubscriptionDataItem = {
                    location: transformedLocationArray,
                    subscription: subscription.SubscriptionType === 1 ? "day" : "month",
                    washname: subscription.WashName,
                    washdetails: subscription.SubscriptionDetails,
                    washnumber: subscription?.NumberOfWashes?.toString() || '0',
                    maxwashperday: subscription?.DailyWashLimit?.toString() || '0',
                    amount: subscription?.Price?.toString() || '0',
                    preAuthAmount: subscription?.PreAuthAmount != null && Number(subscription.PreAuthAmount) > 0
                        ? subscription.PreAuthAmount.toString()
                        : '',
                    isTrial: (subscription?.isTrial === 1) ? true : false,
                    trialPeriod: subscription.trialPeriod ? subscription.trialPeriod?.toString() : '0',
                    trialAmount: subscription?.trialAmount?.toString() || '0',
                    isFree: (subscription.isTrial && subscription?.trialAmount?.toString() == "0") ? true : false,
                    priceForExtraTime:subscription?.priceForExtraTime?.toString() || '0',
                    washTime: subscription?.WashTime?.toString() || '0',
                    hasVacuumWash: (subscription?.hasVacuumWash === 1) ? true : false,
                    numberOfVacuumWashes:subscription?.numberOfVacuumWashes?.toString() || '0',
                    maxVacuumTime: subscription?.maxVacuumTime?.toString() || '0',
                    userFamilyDiscount:subscription?.userFamilyDiscount || null,
                    canExtentSingleWash:subscription?.canExtentSingleWash,
                    isPriceHoldEnabled: subscription?.isPriceHold === 1 || subscription?.isPriceHold === true,
                    isPriceHoldEmail: subscription?.isPriceHoldEmail === 1 || subscription?.isPriceHoldEmail === true
                }
                setSubscriptionData(subscriptionData)
                 if (subscriptionData?.userFamilyDiscount) {
                setDiscountData(subscriptionData.userFamilyDiscount);
                setIsFamilyPlan(true);
                }
             
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            // throw new Error(error.response?.data?.message || error.message);
        }
    }

    const handleDiscountChange = (e:any) => {
    const { name, value } = e.target;
      // Prevent non-numeric input (allow empty and decimals)
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Please enter a valid number",
      }));
      return;
    }
    setDiscountData((prev) => ({
      ...prev,
      [name]: value
    }));

       if (!value.trim()) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Family plan amount is required",
      }));
    } else if (isNaN(Number(value))) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Please enter a valid number",
      }));
    } else {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };


    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setSubscriptionData({
            ...subscriptionData,
            [name]: value,
        });

    };

    const handleMultiSelectChange = (selectedOption: [], name: string) => {
        setSelectedLocation(selectedOption)
        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setSubscriptionData({
            ...subscriptionData,
            [name]: selectedOption,
        });


    }

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {

        const { name, value } = event.target

        // if (name === 'amount') {
        //     if (subscriptionData.subscription != "day" && fullEditAllowed === false) return;
        // }

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'amount' || name === 'preAuthAmount') {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^\d{0,8}(\.\d{0,2})?$/
                ;

            if (regex.test(value)) {
                setSubscriptionData({
                    ...subscriptionData,
                    [name]: value,
                });
            }
        } 

        else {
            setSubscriptionData({
                ...subscriptionData,
                [name]: value,
            });
        }
    }

        const validateFamilyPlan = (): boolean => {
    const newErrors: any = {};
        if(discountData)
        {
            Object.entries(discountData).forEach(([key, value]) => {
            if (!value) {
                newErrors[key as keyof DiscountData] = "This field is required";
            }else if (isNaN(Number(value)))  {
                newErrors[key as keyof DiscountData] = "Please enter a valid number";
            }
        
            });
        }
    
    setFamilyPlanErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            const isFamilyplanEmpty = discountData ? Object.values(discountData).every(
                (value) => value === "" || value === null
                ) : true;
                if(isFamilyPlan &&   !validateFamilyPlan()) 
                {
                    return
                }

            if(currentBayType==serviceType.SelfServe)
                {
                await addSelfServeOnlySubscriptionSchema.validate(subscriptionData, { abortEarly: false });
                }
                else{
                await addSelfServeSubscriptionSchema.validate(subscriptionData, { abortEarly: false });
                }

      
            const locationIdArray = subscriptionData.location && subscriptionData.location.map(loc => loc.value)
            const newData = 
            // (fullEditAllowed === false) ? {
            //     subscriptionId: param.id ? parseInt(param.id) : 0,
            //     washName: subscriptionData.washname,
            //     subscriptionDetails: subscriptionData.washdetails,
            //     isTrial: (subscriptionData.isTrial) ? 1 : 0,
            //     trialPeriod: (subscriptionData.isTrial && subscriptionData.trialPeriod) ? Number(subscriptionData.trialPeriod) : 0,
            //     trialAmount: (subscriptionData.trialAmount && subscriptionData.isTrial) ? parseFloat(subscriptionData.trialAmount) : 0,
            //     price: Number(subscriptionData.amount),
            //     dailyWashLimit: (subscriptionData.maxwashperday ) ? Number(subscriptionData.maxwashperday) : 1,
            //     numberOfWashes:subscriptionData.washnumber,
            // } : 
            {
                locationIds: locationIdArray,
                subscriptionId: param.id ? parseInt(param.id) : 0,
                washName: subscriptionData.washname,
                subscriptionDetails: subscriptionData.washdetails,
                isTrial: (subscriptionData.isTrial) ? 1 : 0,
                trialPeriod: (subscriptionData.isTrial && subscriptionData.trialPeriod) ? Number(subscriptionData.trialPeriod) : 0,
                trialAmount: (subscriptionData.trialAmount && subscriptionData.isTrial) ? parseFloat(subscriptionData.trialAmount) : 0,
                price: Number(subscriptionData.amount),
                // TEMP: Pre-Auth Amount - may need in future
                // ...(subscriptionData.subscription === "day" ? { preAuthAmount: subscriptionData.preAuthAmount ? parseFloat(subscriptionData.preAuthAmount) : 0 } : {}),
                dailyWashLimit: (subscriptionData.maxwashperday ) ? Number(subscriptionData.maxwashperday) : 1,
                numberOfWashes: subscriptionData.washnumber,
                washTime:  Number(subscriptionData.washTime),
                priceForExtraTime: subscriptionData?.canExtentSingleWash ? Number(subscriptionData.priceForExtraTime) : 0,
                hasVacuumWash: subscriptionData?.hasVacuumWash ,
                numberOfVacuumWashes:Number(subscriptionData?.numberOfVacuumWashes ?? 0),
                maxVacuumTime: Number(subscriptionData?.maxVacuumTime ?? 0),
                userFamilyDiscount : isFamilyplanEmpty ? null : discountData,
                canExtentSingleWash: subscriptionData?.canExtentSingleWash,
                isPriceHoldEnabled: subscriptionData.isPriceHoldEnabled ? true : false,
                isPriceHoldEmail: subscriptionData.isPriceHoldEnabled && subscriptionData.isPriceHoldEmail ? true : false

                // discountData && !isFamilyplanEmpty ? discountData : null,

            }


            try {
                setLoading(true)
                const { data } = (!carwash_owner) ?
                    await axios.post(SubscriptionApi.SUBSCRIPTION_EDIT, newData)
                    : await axios.post(SubscriptionApi.CARWASH_OWNER_SUBSCRIPTION_EDIT, newData)
                    setLoading(false)
                if (data?.error === 0) {
                    if (!carwash_owner) {
                        const redirectUrl = (fullEditAllowed === true && data?.data?.is_same_locations === false) ? `/superadmin/${pageURLs.add_relaynumber}/${param.carWashId}/${newData.subscriptionId}` : `/superadmin/${pageURLs.manage_subscriptions}/${param.carWashId}`
                        navigate(redirectUrl)
                    } else {
                        const redirectUrl = (fullEditAllowed === true && data?.data?.is_same_locations === false) ? `/${pageURLs.add_relaynumber}/${newData.subscriptionId}` : `/${pageURLs.manage_subscriptions}?bayType=${currentBayType}`
                        navigate(redirectUrl)
                    }
                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }


        } catch (error) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    if (err.path) {
                        newErrors[err.path as keyof typeof subscriptionData] = err.message;
                    }
                });
                // Fallback when abortEarly yields a single error without inner entries
                if (Object.keys(newErrors).length === 0 && error.path) {
                    newErrors[error.path as keyof typeof subscriptionData] = error.message;
                }
                setErrors((prev) => {
                    const cleared = Object.keys(prev).reduce((acc, key) => {
                        acc[key as keyof typeof prev] = '';
                        return acc;
                    }, { ...prev });
                    return { ...cleared, ...newErrors };
                });
            }

            // Focus on the first invalid field
            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        }
    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    // Use useEffect to focus on the first editable field when the form loads
    // useEffect(() => {
    //     if (formRef.current) {

    //         console.log(formRef.current)
    //         // Find the first editable field
    //         const firstEditableElement = formRef.current.querySelector<HTMLInputElement | HTMLSelectElement | HTMLDivElement>(
    //             'input, select, textarea'
    //         );
    //         if (firstEditableElement) {
    //             firstEditableElement.focus();
    //         }
    //     }
    // }, []);


    const options = [
        { label: 'Select', value: '0' },
        { label: 'One Month', value: '1' },
        { label: 'Two Months', value: '2' },
        { label: 'Three Months', value: '3' },
        { label: 'Four Months', value: '4' },
        { label: 'Five Months', value: '5' },
        { label: 'Six Months', value: '6' },
        { label: 'Seven Months', value: '7' },
        { label: 'Eight Months', value: '8' },
        { label: 'Nine Months', value: '9' },
        { label: 'Ten Months', value: '10' },
        { label: 'Eleven Months', value: '11' },
        { label: 'Twelve Months', value: '12' },
    ];

    const setTrailPeriodData = async (e: any) => {
        if (e.target.name === 'isTrial') {
            if (e.target.checked === true) {
                setSubscriptionData({
                    ...subscriptionData,
                    [e.target.name]: e.target.checked,
                });
            } else {
                setSubscriptionData({
                    ...subscriptionData,
                    [e.target.name]: e.target.checked,
                    ['trialPeriod']: '0',
                    ['isFree']: false,
                    ['trialAmount']: '',
                });
            }
        } else if (e.target.name === 'isFree') {
            setErrors({
                ...errors,
                ["trialAmount"]: ""
            })
            if (e.target.checked === true) {
                setSubscriptionData({
                    ...subscriptionData,
                    [e.target.name]: e.target.checked,
                    ['trialAmount']: '0',
                });
            } else {
                setSubscriptionData({
                    ...subscriptionData,
                    [e.target.name]: e.target.checked,
                    ['trialAmount']: '0',
                });
            }
        }
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                <Breadcrumb>
                    {props.userType !== userType["CARWASH-OWNER"] ?
                        <>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                            {companyDetails && (<Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.carwash_locations}/${param.carWashId}`) }}  > {companyDetails.CompanyName}</Breadcrumb.Item>)}
                        </>
                        : null}

                    <Breadcrumb.Item onClick={() =>
                        itemClick(
                            props.userType !== userType["CARWASH-OWNER"]
                                ? `/superadmin/${pageURLs.manage_subscriptions}/${param.carWashId}`
                                : `/${pageURLs.manage_subscriptions}?type=${subscription_type}&bayType=${currentBayType}`
                        )
                    }>
                        {subscription_type === 'day' ? (currentBayType==serviceType.Vacuum ? "Manage Single Use" :"Manage Single Wash") : 'Manage Subscriptions'}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>{subscription_type === 'day' ? (currentBayType==serviceType.Vacuum ? "Edit Single Use" :"Edit Single Wash") : 'Edit Subscription'}</Breadcrumb.Item>
                </Breadcrumb>
                {props.userType !== userType["CARWASH-OWNER"] ?

                    <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                        {companyDetails && <GenerateLink id={param.id} companyDetails={companyDetails} carwash_owner={carwash_owner} />}
                        <div className="activity-status d-flex">
                            <CarWashStatus carWashId={param.id} status={(companyDetails?.IsCompanyActive == 1) ? true : false} companyName={companyDetails.CompanyName} />
                            <div className="stat-group d-flex align-items-center">
                                <p>Subscription Status</p>
                                <Badge className='active'>Active</Badge>
                            </div>

                        </div>
                    </div> : null}
                <div className='cw-form-outer-wrap'>
                    <form onSubmit={(e) => handleSubmit(e)} ref={formRef}>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">
                                {/* <CWSelect
                                    id="location"
                                    name="location"
                                    options={locations}
                                    label="Select Location"
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    isMandatory
                                    placeholder="Select Location"
                                    multi={true}
                                    errorMessage={errors.location}
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'location')}
                                /> */}
                                <div>
                                    <CWMultiSelect
                                        id="location"
                                        name="location"
                                        options={locations}
                                        label="Select Location"
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        isMandatory
                                        placeholder="Select Location"
                                        errorMessage={errors.location}
                                        onChange={(selectedOptions) => handleMultiSelectChange(selectedOptions, "location")}
                                        hasSelectAll={true}
                                        value={subscriptionData.location || selectedLocation}//Correction required
                                        labelAllSelected="All Locations Selected."
                                        disabled={true}
                                        // disabled ={fullEditAllowed === true ? false : true}
                                        />
                                </div>
                                <CWSelect
                                    id="subscription"
                                    name="subscription"
                                    options={subscriptionTypes}
                                    label="Select Subscription Type"
                                    isMandatory
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select Subscription Type"
                                    multi={false}
                                    values={subscriptionTypes.filter(option => option.value === subscriptionData.subscription)}
                                    errorMessage={errors.subscription}
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'subscription')}
                                    disabled 
                                />
                                <Row>
                                    <Col md={12}>
                                        <CWInput
                                            id="washname"
                                            name="washname"
                                            type="text"
                                            label={currentBayType==serviceType.Vacuum ? "Vacuum Name": "Wash Name"}
                                            maxLength={WASH_NAME_MAX_LENGTH}
                                            isMandatory
                                            value={subscriptionData.washname}
                                            errorMessage={(errors?.washname && errors?.washname!="" ) && currentBayType==serviceType.Vacuum ? "Enter Vacuum Name":errors.washname}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    {/* <Col md={6}>
                                        <CWTextarea
                                            id="washdetails"
                                            name="washdetails"
                                            type="text"
                                            label="Wash Details"
                                            maxLength={WASH_DETAIL_MAX_LENGTH}
                                            isMandatory
                                            value={subscriptionData.washdetails}
                                            errorMessage={errors.washdetails}
                                            onChange={handleInputChange}
                                        />
                                    </Col> */}
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <div className="custom-note position-relative">
                                            <CWInput
                                                id="washTime"
                                                name="washTime"
                                                type="number"
                                                label="Select Time (Minutes)"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={subscriptionData.washTime}
                                                errorMessage={errors.washTime  && currentBayType==serviceType.Vacuum ? "Enter valid vacuum time.":errors.washTime}
                                                onChange={handleInputChange}
                                                // readOnly={formData.subscription === "day"}
                                            />
                                            {/* <CWInput
                                                id="washnumber"
                                                name="washnumber"
                                                type="number"
                                                label="Number of Washes"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={subscriptionData.subscription === "day" ? 1 : subscriptionData.washnumber}
                                                errorMessage={errors.washnumber}
                                                onChange={handleInputChange}
                                                // readOnly ={fullEditAllowed === true ? false : true}
                                                

                                            /> */}
                                            {/* <span className="note"><i>*Single Wash value will be set to 1 by default</i></span> */}
                                        </div>
                                    </Col>

                                
                                    <Col md={6}>
                                        <CWInputGroup
                                            id="amount"
                                            name="amount"
                                            type="number"
                                            step='0.01'
                                            label="Amount"
                                            labelPrefix="$"
                                            isMandatory
                                            maxLength={AMOUNT_MAX_LENGTH}
                                            value={subscriptionData.amount}
                                            errorMessage={errors.amount}
                                            onChange={handleInputChange}
                                            // readOnly={subscriptionData.subscription !== "day" && fullEditAllowed === false}
                                        />
                                    </Col>
                                </Row>
                                {subscriptionData.subscription === "month" ? (
                                    <Row>
                                        <Col md={6}>
                                            <CWCheckbox
                                                id="isPriceHoldEnabled"
                                                name="isPriceHoldEnabled"
                                                label="60-Day Price Hold After Update"
                                                checked={subscriptionData.isPriceHoldEnabled}
                                                onChange={(e) => {
                                                    setSubscriptionData({
                                                        ...subscriptionData,
                                                        [e.target.name]: e.target.checked,
                                                        isPriceHoldEmail: e.target.checked ? subscriptionData.isPriceHoldEmail : false,
                                                    });
                                                }}
                                            />
                                        </Col>
                                        {subscriptionData.isPriceHoldEnabled ? (
                                            <Col md={12}>
                                                <CWCheckbox
                                                    id="isPriceHoldEmail"
                                                    name="isPriceHoldEmail"
                                                    label="Notify customers by email about subscription price updates"
                                                    helptext="Checked: Send an email notification to customers. Unchecked: No email notification will be sent."
                                                    checked={subscriptionData.isPriceHoldEmail}
                                                    onChange={(e) => {
                                                        setSubscriptionData({
                                                            ...subscriptionData,
                                                            [e.target.name]: e.target.checked,
                                                        });
                                                    }}
                                                />
                                            </Col>
                                        ) : null}
                                    </Row>
                                ) : null}
                                {subscriptionData.subscription === "day" && currentBayType==serviceType.Vacuum ?
                                <Row>
                                        <Col md={6}>
                                            <CWCheckbox
                                        id="hasExtension"
                                        name="hasExtension"
                                        label="Additional Time"
                                        checked={subscriptionData.canExtentSingleWash == 1 ? true : false}
                                        onChange={(e) => {setHasExtension(e) }}
                                    />
                                        </Col>
                                </Row>
                                        :""}
                                    
                                {subscriptionData.subscription === "day" ? (
                                <Row>
                                    {subscriptionData.canExtentSingleWash ? (
                                        <Col md={6}>
                                            <CWInput
                                                id="priceForExtraTime"
                                                name="priceForExtraTime"
                                                type="number"
                                                label="Cost per each additional minute"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={subscriptionData.priceForExtraTime}
                                                errorMessage={errors.priceForExtraTime}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    ) : null}
                                    {/* TEMP: Pre-Auth Amount - may need in future
                                    <Col md={6}>
                                        <CWInputGroup
                                            id="preAuthAmount"
                                            name="preAuthAmount"
                                            type="number"
                                            step='0.01'
                                            label="Pre-Auth Amount"
                                            labelSuffix="(It is recommended to set the pre-authorization amount to be greater than or equal to the maximum wash amount.)"
                                            labelPrefix="$"
                                            isMandatory
                                            maxLength={AMOUNT_MAX_LENGTH}
                                            value={subscriptionData.preAuthAmount ?? ''}
                                            errorMessage={errors.preAuthAmount}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    */}
                                </Row>
                                ) : null}
                                                                      {/* free vacuum wash */}
{currentBayType!=serviceType.Vacuum ? <div className="trial-sec mt-4">
                                    <CWCheckbox
                                        id="hasVacuumWash"
                                        name="hasVacuumWash"
                                        label="Offer Free Vacuum / Mat Washer"
                                        checked={subscriptionData.hasVacuumWash}
                                        onChange={(e) => { setFreeVacuumData(e); }}
                                    />
                                    {subscriptionData.hasVacuumWash ? <Row>
                                       {subscriptionData.subscription === 'month' ? <Col md={6}>
                                            <CWInput
                                                id="numberOfVacuumWashes"
                                                name="numberOfVacuumWashes"
                                                label="Number of Times per Month"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={subscriptionData.numberOfVacuumWashes}
                                                errorMessage={errors.numberOfVacuumWashes}
                                                onChange={handleInputChange}
                                            />
                                        </Col> :""}
                                        <Col md={6}>
                                                <CWInput
                                                id="maxVacuumTime"
                                                name="maxVacuumTime"
                                                label="Maximum Time per Use (in minutes)"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={subscriptionData.maxVacuumTime}
                                                errorMessage={errors.maxVacuumTime}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </Row> : ''}
                                </div> : ''}
                                        {subscriptionData.subscription === "month" && (
                                        <CWCheckbox
                                        id="Isfamilyplan"
                                        name="Isfamilyplan"
                                        label="Family Plan"
                                        checked={isFamilyPlan}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setIsFamilyPlan(checked);
                                            if (!checked) {
                                            setDiscountData(null);
                                            setFamilyPlanErrors({});
                                            }
                                        }}
                                        />
                                    )}

                                    {isFamilyPlan && (
                                        <Row>
                                        {[...Array(10)].map((_, index) => {
                                            const key = `discount${index + 1}`;
                                            return (
                                            <Col md={4} key={key}>
                                                <CWInputGroup
                                                id={key}
                                                name={key}
                                                type="number"
                                                step='0.01'
                                                label={`Amount to be paid for Family Plan (Sub User ${index + 1}${index === 0 ? ', not account owner' : ''})`}
                                                labelPrefix="$"
                                                maxLength={AMOUNT_MAX_LENGTH}
                                                value={discountData && discountData[key] || ""}
                                                errorMessage={familyPlanErrors[key]}
                                                onChange={handleDiscountChange}
                                                />
                                            </Col>
                                            );
                                        })}
                                        </Row>
                                    )}
                             
                            </div>
                        </div>
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => props.userType !== userType["CARWASH-OWNER"] ? navigate(`/superadmin/${pageURLs.manage_subscriptions}/${param.carWashId}`) : navigate(`/${pageURLs.manage_subscriptions}?bayType=${currentBayType}`)}>Cancel</Button>
                            <Button title="Save" variant="primary" className="primary-btn" type="submit">Save</Button>
                        </div>
                    </form>
                </div>
            </Container>

            {loading && <SmallLoader />}

        </div >
    )
}

export default EditSelfServeSubscription;
